import { Link } from "react-router-dom";
import { Wrench, CalendarClock, CalendarCheck2, Phone, ChevronRight } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { KindIcon } from "@/components/Badges";
import { formatDate, daysText } from "@/lib/format";
import { KIND_LABEL } from "@/lib/calendar";
import { cn } from "@/lib/utils";
import { EventActionsMenu } from "./EventActions";

const shortDate = (iso) => (iso ? formatDate(iso).slice(0, 5) : "");

/** «Planlagt 25.09» – slate, aldri varselfarge */
export const PlannedBadge = ({ date, className, size = "sm" }) => (
  <span
    data-testid="planned-badge"
    className={cn(
      "status-planned inline-flex shrink-0 items-center gap-1 rounded-full border font-semibold whitespace-nowrap",
      size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
      className
    )}
  >
    <CalendarCheck2 className="h-3 w-3" /> Planlagt {shortDate(date)}
  </span>
);

/** Kompakt chip i månadsruter. planned=true teiknar planlagt-markør i staden for frist. */
export const EventChip = ({ e, planned = false, onClick, dim = false }) => {
  const Icon = e.kind === "annual" ? CalendarClock : Wrench;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`calendar-chip-${e.machine_id}-${e.kind}${planned ? "-planned" : ""}`}
      title={`${e.label} · ${KIND_LABEL[e.kind]} · ${e.customer_name || ""}`}
      className={cn(
        "flex w-full items-center gap-1 rounded-md border px-1.5 py-[3px] text-left text-[11px] font-semibold leading-tight",
        "transition-[transform,opacity] duration-150 hover:-translate-y-px",
        planned ? "status-planned border-dashed" : `status-${e.status}`,
        dim && "opacity-60"
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{planned ? `Plan: ${e.label}` : e.label}</span>
    </button>
  );
};

/**
 * Full rad i lister/agenda/dagpanel.
 * mode: "due" (standard) | "planned" (rada representerer planlagt dato)
 * compact: stabla layout for smale panel (dagpanel/sheet)
 */
export const EventRow = ({ e, today, mode = "due", compact = false, onPlan, onMove, onUnplan, className, showCustomer = true }) => {
  const days = e.days_remaining;
  const dateText = `${mode === "planned" ? `Frist ${formatDate(e.date)}` : formatDate(e.date)}${
    days !== null && days !== undefined && mode === "due" ? ` (${daysText(days)})` : ""
  }`;

  if (compact) {
    return (
      <div
        data-testid={`calendar-event-${e.machine_id}-${e.kind}`}
        className={cn("group flex items-start gap-3 rounded-xl border border-border bg-card p-3 pr-1.5 shadow-sm transition-colors hover:bg-accent/60", className)}
      >
        <KindIcon kind={e.kind} className="mt-0.5" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Link to={`/maskiner/${e.machine_id}`} className="block text-sm font-semibold leading-snug text-foreground hover:underline" data-testid={`calendar-event-link-${e.machine_id}-${e.kind}`}>
            {e.label}
          </Link>
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">{KIND_LABEL[e.kind]}</span>
            {showCustomer && e.customer_name ? ` · ${e.customer_name}` : ""}
          </div>
          <div className="text-xs tabular-nums text-muted-foreground">{dateText}</div>
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <StatusPill status={e.status} />
            {e.planned_date && mode === "due" && <PlannedBadge date={e.planned_date} />}
          </div>
        </div>
        <EventActionsMenu e={e} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
      </div>
    );
  }

  return (
    <div
      data-testid={`calendar-event-${e.machine_id}-${e.kind}`}
      className={cn(
        "group flex items-center gap-3 rounded-xl border border-border bg-card p-2.5 pr-1.5 shadow-sm transition-colors hover:bg-accent/60 sm:p-3 sm:pr-2",
        className
      )}
    >
      <KindIcon kind={e.kind} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link
            to={`/maskiner/${e.machine_id}`}
            className="truncate text-sm font-semibold text-foreground hover:underline"
            data-testid={`calendar-event-link-${e.machine_id}-${e.kind}`}
          >
            {e.label}
          </Link>
          {e.planned_date && mode === "due" && <PlannedBadge date={e.planned_date} />}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{KIND_LABEL[e.kind]}</span>
          {showCustomer && e.customer_name && (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{e.customer_name}</span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">{dateText}</span>
        </div>
      </div>
      <StatusPill status={e.status} className="hidden sm:inline-flex" />
      <StatusPill status={e.status} label="" className="sm:hidden !gap-0 px-1.5" />
      <EventActionsMenu e={e} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
    </div>
  );
};

/** Kundegruppe-header i agenda (kunderunde) */
export const CustomerGroupHeader = ({ g, onFilter }) => (
  <div className="flex items-center gap-3 rounded-xl bg-muted/60 px-3 py-2.5" data-testid={`calendar-customer-group-${g.customer_id || "ukjend"}`}>
    <div className="min-w-0 flex-1">
      <div className="truncate font-heading text-sm font-bold">{g.customer_name}</div>
      <div className="text-xs text-muted-foreground">
        {g.items.length} {g.items.length === 1 ? "frist" : "fristar"} · {g.machines} {g.machines === 1 ? "maskin" : "maskiner"} · første {formatDate(g.first)}
      </div>
    </div>
    {g.customer_phone && (
      <a
        href={`tel:${String(g.customer_phone).replace(/\s+/g, "")}`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border bg-card text-foreground transition-colors hover:bg-accent"
        aria-label={`Ring ${g.customer_name}`}
        data-testid={`calendar-customer-call-${g.customer_id}`}
      >
        <Phone className="h-4 w-4" />
      </a>
    )}
    {onFilter && (
      <button
        type="button"
        onClick={() => onFilter(g.customer_id)}
        className="inline-flex h-9 items-center gap-1 rounded-lg border bg-card px-2.5 text-xs font-semibold transition-colors hover:bg-accent"
        data-testid={`calendar-customer-filter-${g.customer_id}`}
      >
        Vis <ChevronRight className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);
